import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Home from "@/pages/Home";
import DesignDurable from "@/pages/DesignDurable";
import InnovationResponsable from "@/pages/InnovationResponsable";
import StrategieClimat from "@/pages/StrategieClimat";
import StrategieRSE from "@/pages/StrategieRSE";
import Contact from "@/pages/Contact";
import Podcast from "@/pages/Podcast";
import Dashboard from "@/pages/Dashboard";
import ModuleViewer from "@/pages/ModuleViewer";
import AdminModules from "@/pages/AdminModules";
import AdminContent from "@/pages/AdminContent";
import Auth from "@/pages/Auth";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--alma-cream)]">
        <div className="animate-spin w-8 h-8 border-4 border-[var(--alma-forest)] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("alma_token"));

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("alma_token");
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data.user);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem("alma_token");
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("alma_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, company) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name, 
      company 
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("alma_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("alma_token");
    setToken(null);
    setUser(null);
  };

  const authValue = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={authValue}>
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <Navbar />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/design-durable" element={<DesignDurable />} />
              <Route path="/innovation-responsable" element={<InnovationResponsable />} />
              <Route path="/strategie-climat" element={<StrategieClimat />} />
              <Route path="/strategie-rse" element={<StrategieRSE />} />
              <Route path="/podcast" element={<Podcast />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/auth" element={<Auth />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/modules/:id" 
                element={<ModuleViewer />} 
              />
              <Route 
                path="/admin/modules" 
                element={
                  <ProtectedRoute>
                    <AdminModules />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/content" 
                element={
                  <ProtectedRoute>
                    <AdminContent />
                  </ProtectedRoute>
                } 
              />
            </Routes>
          </main>
          <Footer />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
